/**
 * Мини-график, который можно и открыть, и «прощупать».
 *
 * Два жеста на одном элементе, разведённые по намерению:
 *
 * * **тап** — переход туда, где эта метрика живёт целиком;
 * * **нажатие с проведением** — значение под пальцем прямо здесь, без ухода
 *   с экрана. Это и есть «посмотреть период, не открывая»: чаще всего
 *   вопрос звучит «а что было в среду», и ради него незачем менять экран.
 *
 * Различаются они не таймером, а СМЕЩЕНИЕМ: сдвинул палец — значит щупаешь,
 * отпустил на месте — значит открываешь. Таймер заставлял бы ждать и
 * наказывал бы за быстрый жест; смещение читается мгновенно и не зависит от
 * того, как быстро человек двигает рукой.
 */
import { useCallback, useRef, useState } from "react";

import { Sparkline, type SparklineProps } from "../canon/Sparkline";

/** Дальше этого сдвига жест считается прощупыванием, а не тапом. */
const SCRUB_SLOP_PX = 6;

export interface ScrubPoint {
  index: number;
  value: number;
  /** Доля позиции [0..1] — для подписи оси, если она нужна вызывающему. */
  at: number;
}

export function ScrubbableSparkline({
  values,
  onOpen,
  formatValue,
  label,
  ...sparkProps
}: {
  values: number[];
  /** Тап без смещения. Не задан — элемент остаётся только прощупываемым. */
  onOpen?: () => void;
  /** Как показать значение под пальцем. По умолчанию — округление. */
  formatValue?: (p: ScrubPoint) => string;
  label?: string;
} & Omit<SparklineProps, "values">) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const downX = useRef<number | null>(null);
  const moved = useRef(false);
  // Компонент часто живёт ВНУТРИ ссылки (плитка метрики целиком кликабельна).
  // Там проведение пальцем по графику заканчивается обычным click, и браузер
  // уводит со страницы, хотя человек только смотрел значения. Флаг гасит
  // именно тот click, который последовал за прощупыванием.
  const suppressClick = useRef(false);
  const [scrub, setScrub] = useState<ScrubPoint | null>(null);

  const pointAt = useCallback(
    (clientX: number): ScrubPoint | null => {
      const el = hostRef.current;
      if (!el || values.length === 0) return null;
      const rect = el.getBoundingClientRect();
      if (rect.width <= 0) return null;
      const at = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const index = Math.round(at * (values.length - 1));
      return { index, value: values[index], at };
    },
    [values],
  );

  const show = useCallback((clientX: number) => setScrub(pointAt(clientX)), [pointAt]);

  const interactive = !!onOpen || values.length > 1;

  return (
    <div
      ref={hostRef}
      data-testid="scrubbable-sparkline"
      className={"relative" + (interactive ? " touch-none select-none" : "")}
      role={onOpen ? "button" : undefined}
      tabIndex={onOpen ? 0 : undefined}
      aria-label={label}
      onKeyDown={(e) => {
        if (!onOpen) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      onPointerDown={(e) => {
        downX.current = e.clientX;
        moved.current = false;
        e.currentTarget.setPointerCapture?.(e.pointerId);
      }}
      onPointerMove={(e) => {
        if (downX.current == null) return;
        if (Math.abs(e.clientX - downX.current) > SCRUB_SLOP_PX) moved.current = true;
        if (moved.current) show(e.clientX);
      }}
      onPointerUp={(e) => {
        e.currentTarget.releasePointerCapture?.(e.pointerId);
        // Отпустил, не сдвинув, — это открытие. Сдвинул — только смотрел.
        if (!moved.current && onOpen) onOpen();
        suppressClick.current = moved.current;
        downX.current = null;
        moved.current = false;
        setScrub(null);
      }}
      onClickCapture={(e) => {
        if (!suppressClick.current) return;
        suppressClick.current = false;
        e.preventDefault();
        e.stopPropagation();
      }}
      onPointerCancel={() => {
        downX.current = null;
        moved.current = false;
        setScrub(null);
      }}
      onPointerLeave={() => setScrub(null)}
    >
      <Sparkline values={values} {...sparkProps} />

      {scrub && (
        <>
          {/* Отвес по позиции пальца — глазу нужна привязка значения к месту */}
          <span
            className="absolute inset-y-0 w-px bg-fg-muted pointer-events-none"
            style={{ left: `${scrub.at * 100}%` }}
            aria-hidden
          />
          <span
            data-testid="sparkline-scrub-value"
            className="absolute -top-1 num-display text-[10px] text-fg-primary bg-bg-card px-1 rounded pointer-events-none whitespace-nowrap"
            // Подпись у края уехала бы за пределы карточки, поэтому у правой
            // половины она разворачивается влево от отвеса.
            style={
              scrub.at > 0.6
                ? { right: `${(1 - scrub.at) * 100}%` }
                : { left: `${scrub.at * 100}%` }
            }
          >
            {formatValue ? formatValue(scrub) : Math.round(scrub.value)}
          </span>
        </>
      )}
    </div>
  );
}
